import { ConsultantNode } from '../types/career';
import { countTotalConsultants } from '../utils/treeUtils';
import { getCurrentSessionToken, getStoredUser } from './auth';

export interface SavedStructure {
  id: string;
  consultantName: string;
  customTitle?: string;
  level: number;
  teamSize: number;
  totalMonthlyAE: number;
  totalCumulativeAE: number;
  savedAt: string;
  tree: ConsultantNode;
}

export const LEGACY_STORAGE_KEY = 'starlife_structure_library_v1';
export const LEGACY_MIGRATED_KEY = 'starlife_legacy_structures_migrated';

export function getAccountStorageKey(accountId?: string): string {
  const user = getStoredUser();
  const id = accountId || user?.accountId;
  return id ? `starlife_structure_library_v1_${id}` : 'starlife_structure_library_v1_guest';
}

const memoryStore = new Map<string, string>();

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getCurrentSessionToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function getStorageItem(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.getItem === 'function') {
      const val = localStorage.getItem(key);
      if (val !== null) return val;
    }
  } catch {
    // Ignore
  }
  return memoryStore.get(key) || null;
}

function setStorageItem(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.setItem === 'function') {
      localStorage.setItem(key, value);
    }
  } catch {
    // Ignore
  }
  memoryStore.set(key, value);
}

function removeStorageItem(key: string): void {
  try {
    if (typeof localStorage !== 'undefined' && localStorage && typeof localStorage.removeItem === 'function') {
      localStorage.removeItem(key);
    }
  } catch {
    // Ignore
  }
  memoryStore.delete(key);
}

export function setRawStorageItem(key: string, value: string): void {
  setStorageItem(key, value);
}

export function getRawStorageItem(key: string): string | null {
  return getStorageItem(key);
}

/**
 * Clear memory cache (called on logout/session switch).
 */
export function clearSavedStructuresCache(): void {
  memoryStore.clear();
}

/**
 * Clear saved structures for the active account (or specified account).
 */
export function clearSavedStructures(accountId?: string): void {
  const key = getAccountStorageKey(accountId);
  removeStorageItem(key);
  removeStorageItem(LEGACY_STORAGE_KEY);
  memoryStore.delete(key);
  memoryStore.delete(LEGACY_STORAGE_KEY);
}

/**
 * Retrieve user-saved structures from account-isolated local storage cache.
 */
export function getSavedStructures(accountId?: string): SavedStructure[] {
  try {
    const key = getAccountStorageKey(accountId);
    let raw = getStorageItem(key);

    // Backward-compatibility: if admin has no scoped cache yet, check legacy storage
    if (!raw) {
      const user = getStoredUser();
      const isAdmin = user?.role === 'admin' && user?.accountId === 'admin_sergey';
      if (isAdmin) {
        raw = getStorageItem(LEGACY_STORAGE_KEY);
      }
    }

    if (!raw) return [];
    return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to load saved structures from storage:', e);
    return [];
  }
}

/**
 * Fetch structures from Cloudflare KV for the current account and update isolated local cache.
 * Restricts legacy data migration exclusively to Admin Sergey's initial account.
 */
export async function fetchCloudStructures(accountId?: string): Promise<SavedStructure[]> {
  const targetKey = getAccountStorageKey(accountId);

  try {
    const res = await fetch('/api/structures', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (res.ok) {
      const data = (await res.json()) as { success: boolean; structures?: SavedStructure[] };
      if (data.success && Array.isArray(data.structures)) {
        const cloudStructures = data.structures;

        // Check if legacy migration should run: ONLY for Admin Sergey when cloud is empty
        const user = getStoredUser();
        const isAdmin = user?.role === 'admin' && user?.accountId === 'admin_sergey';
        const isLegacyMigrated = getStorageItem(LEGACY_MIGRATED_KEY) === 'true';

        if (isAdmin && !isLegacyMigrated && cloudStructures.length === 0) {
          const legacyRaw = getStorageItem(LEGACY_STORAGE_KEY);
          if (legacyRaw) {
            try {
              const legacyList = JSON.parse(legacyRaw);
              if (Array.isArray(legacyList) && legacyList.length > 0) {
                for (const item of legacyList) {
                  try {
                    await fetch('/api/structures', {
                      method: 'POST',
                      headers: getAuthHeaders(),
                      body: JSON.stringify({ structure: item }),
                    });
                  } catch {
                    // Ignore single item failure
                  }
                }
                setStorageItem(LEGACY_MIGRATED_KEY, 'true');
                setStorageItem(targetKey, JSON.stringify(legacyList));
                removeStorageItem(LEGACY_STORAGE_KEY);
                return legacyList;
              }
            } catch {
              // Ignore parse error
            }
          }
        }

        // Cache cloud structures in account-isolated key
        setStorageItem(targetKey, JSON.stringify(cloudStructures));
        return cloudStructures;
      }
    }
  } catch {
    // Dev environment or offline: fall back to local storage
  }

  return getSavedStructures(accountId);
}

/**
 * Save a new structure into the library with the consultant's name and metadata.
 * Optimistically updates account-isolated local cache.
 */
export function saveStructureToLibrary(
  tree: ConsultantNode,
  customTitle?: string,
  options: { syncToCloud?: boolean; accountId?: string } = { syncToCloud: true }
): SavedStructure {
  const targetKey = getAccountStorageKey(options.accountId);
  const list = getSavedStructures(options.accountId);
  const teamSize = countTotalConsultants(tree);
  const newEntry: SavedStructure = {
    id: `lib-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    consultantName: tree.name,
    customTitle: customTitle?.trim() || `${tree.name} (Р${tree.level})`,
    level: tree.level,
    teamSize,
    totalMonthlyAE: tree.totalMonthlyAE,
    totalCumulativeAE: tree.totalCumulativeAE,
    savedAt: new Date().toISOString(),
    tree,
  };

  const updated = [newEntry, ...list];
  setStorageItem(targetKey, JSON.stringify(updated));

  // Asynchronous cloud sync (fire-and-forget only if explicitly requested)
  if (options.syncToCloud) {
    try {
      fetch('/api/structures', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ structure: newEntry }),
      }).catch(() => {});
    } catch {
      // Ignore offline errors
    }
  }

  return newEntry;
}

/**
 * Async save directly to Cloudflare KV and wait for confirmation.
 * Avoids duplicate background POST by passing syncToCloud: false.
 */
export async function saveStructureToCloud(
  tree: ConsultantNode,
  customTitle?: string
): Promise<SavedStructure> {
  // Save locally first without initiating duplicate background fetch
  const entry = saveStructureToLibrary(tree, customTitle, { syncToCloud: false });
  const targetKey = getAccountStorageKey();

  try {
    const res = await fetch('/api/structures', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ structure: entry }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.structures) {
        setStorageItem(targetKey, JSON.stringify(data.structures));
      }
    }
  } catch {
    // Local save already succeeded
  }
  return entry;
}

/**
 * Delete a saved structure by ID (account-isolated local cache).
 */
export function deleteStructureFromLibrary(
  id: string,
  options: { syncToCloud?: boolean; accountId?: string } = { syncToCloud: true }
): SavedStructure[] {
  const targetKey = getAccountStorageKey(options.accountId);
  const list = getSavedStructures(options.accountId);
  const updated = list.filter((item) => item.id !== id);
  setStorageItem(targetKey, JSON.stringify(updated));

  // Asynchronously sync deletion with Cloudflare KV only if requested
  if (options.syncToCloud) {
    try {
      fetch(`/api/structures?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      }).catch(() => {});
    } catch {
      // Ignore offline errors
    }
  }

  return updated;
}

/**
 * Async delete directly from Cloudflare KV and wait for confirmation.
 * Avoids duplicate DELETE request by passing syncToCloud: false.
 */
export async function deleteStructureFromCloud(id: string): Promise<SavedStructure[]> {
  const updated = deleteStructureFromLibrary(id, { syncToCloud: false });
  const targetKey = getAccountStorageKey();

  try {
    const res = await fetch(`/api/structures?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.structures) {
        setStorageItem(targetKey, JSON.stringify(data.structures));
        return data.structures;
      }
    }
  } catch {
    // Local deletion already succeeded
  }
  return updated;
}

/**
 * Overwrite or update an existing entry in the library.
 */
export function updateStructureInLibrary(
  id: string,
  updatedData: Partial<SavedStructure>,
  options: { syncToCloud?: boolean; accountId?: string } = { syncToCloud: true }
): SavedStructure[] {
  const targetKey = getAccountStorageKey(options.accountId);
  const list = getSavedStructures(options.accountId);
  const updated = list.map((item) => (item.id === id ? { ...item, ...updatedData } : item));
  setStorageItem(targetKey, JSON.stringify(updated));

  if (options.syncToCloud) {
    const target = updated.find((item) => item.id === id);
    if (target) {
      try {
        fetch('/api/structures', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ structure: target }),
        }).catch(() => {});
      } catch {
        // Ignore offline
      }
    }
  }

  return updated;
}

export interface StructureVersion {
  id: string;
  versionNumber: number;
  savedAt: string;
  level: number;
  teamSize: number;
  totalMonthlyAE: number;
  totalCumulativeAE: number;
  tree: ConsultantNode;
  raw: SavedStructure;
}

export interface StructureFolder {
  folderTitle: string;
  consultantName: string;
  latestVersion: StructureVersion;
  versions: StructureVersion[]; // sorted descending by savedAt (newest first)
}

/**
 * Group saved structures by structure name/title into folders containing version history.
 */
export function groupStructuresByFolder(structures: SavedStructure[]): StructureFolder[] {
  const map = new Map<string, SavedStructure[]>();

  for (const s of structures) {
    const key = (s.customTitle || s.consultantName || 'Моя структура').trim();
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key)!.push(s);
  }

  const folders: StructureFolder[] = [];

  for (const [folderTitle, items] of map.entries()) {
    // Sort items chronologically ascending to assign version numbers (1, 2, 3...)
    const chronological = [...items].sort(
      (a, b) => new Date(a.savedAt).getTime() - new Date(b.savedAt).getTime()
    );
    const versions: StructureVersion[] = chronological.map((item, idx) => ({
      id: item.id,
      versionNumber: idx + 1,
      savedAt: item.savedAt,
      level: item.level,
      teamSize: item.teamSize,
      totalMonthlyAE: item.totalMonthlyAE,
      totalCumulativeAE: item.totalCumulativeAE,
      tree: item.tree,
      raw: item,
    }));

    // Reverse so newest version is at the top
    versions.reverse();

    folders.push({
      folderTitle,
      consultantName: items[0].consultantName,
      latestVersion: versions[0],
      versions,
    });
  }

  // Sort folders by latest version timestamp descending
  folders.sort(
    (a, b) => new Date(b.latestVersion.savedAt).getTime() - new Date(a.latestVersion.savedAt).getTime()
  );

  return folders;
}
